import React, { useEffect, useRef, useState } from 'react'
import { AnimatePresence, cubicBezier, motion } from 'framer-motion'

import StreamingMessage, { StreamingResponse } from './streaming-message'
import ChatBubble from './chat-bubble'
import { createSession, sendFeedback } from './api'
import SendIcon from '@/icons/bwi/send.svg'
import CloseIcon from '@/icons/bwi/close.svg'

import { cx } from '@/utils/strings'
import SourceLink from './source-link'
import { dedupeDocuments } from './utils/document'
import CtaLink, { buttonStyles } from '../ui/cta-link'
import { OnyxDocument } from './types'

type ChatMessage = {
  from: 'user' | 'ai'
  state: 'complete' | 'streaming' | 'error'
  message: string
  animated: boolean
  response?: StreamingResponse
}

type StreamingMessage = {
  message: string
  chatSessionId: string
}

export default function AiSearchField() {
  const [conversation, setConversation] = useState<ChatMessage[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const sessionId = useRef<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const [activeSources, setActiveSources] = useState<{ messageId: number; documents: OnyxDocument[] } | null>(null)
  const parentMessageId = useRef<number | undefined>(undefined)
  const [feedbackState, setFeedbackState] = useState<{
    state: 'closed' | 'positive' | 'negative'
    chatMessageId?: number
  }>({ state: 'closed' })

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search)
    const query = queryParams.get('q')
    if (query) {
      handleSendMessage(query)
    }
  }, [])

  useEffect(() => {
    if (!isLoading) inputRef.current?.focus()
  }, [isLoading])

  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const scrollToBottom = () => {
    if (scrollContainerRef.current) {
      const { scrollHeight, clientHeight } = scrollContainerRef.current
      scrollContainerRef.current.scrollTop = scrollHeight - clientHeight
    }
  }
  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollToBottom()
    }
  }, [conversation.length])

  const handleSendMessage = async (message: string) => {
    setConversation((prev) => [...prev, { message, from: 'user', state: 'complete', animated: true }])
    setIsLoading(true)

    try {
      const session = sessionId.current || (await createSession())
      if (!sessionId.current) sessionId.current = session
      setConversation((prev) => [
        ...prev,
        { message, from: 'ai', state: 'streaming', animated: true, parentMessageId: parentMessageId.current },
      ])
    } catch (error: any) {
      setConversation((prev) => [
        ...prev,
        {
          message: 'An unexpected error occurred. Please try again later.',
          state: 'error',
          from: 'ai',
          animated: true,
        },
      ])
    }
  }

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault()
    const formEl = e.currentTarget
    const formData = new FormData(formEl)
    const search = formData.get('search') as string
    if (!search?.trim()) return
    handleSendMessage(search)
    formEl.reset()
  }

  const handleComplete = (data: StreamingResponse) => {
    setIsLoading(false)
    setConversation((prev) => {
      const newConversation = [...prev]
      const lastMessage = newConversation[newConversation.length - 1]
      if (lastMessage.from === 'ai') {
        lastMessage.message = data.answer
        lastMessage.state = 'complete'
        lastMessage.animated = false
        lastMessage.response = data
        parentMessageId.current = data.finalMessage?.message_id
      }
      return newConversation
    })
  }
  const handleError = (error: string) => {
    setIsLoading(false)
    setConversation((prev) => {
      const newConversation = [...prev]
      const lastMessage = newConversation[newConversation.length - 1]
      if (lastMessage.from === 'ai') {
        lastMessage.message = error || 'An unexpected error occurred. Please try again later.'
        lastMessage.state = 'error'
        lastMessage.animated = false
      }
      return newConversation
    })
  }

  const handleSubmitFeedback: React.FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault()
    const formEl = e.currentTarget
    const formData = new FormData(formEl)
    const feedback = formData.get('feedback') as string
    sendFeedback({
      isPositive: feedbackState.state === 'positive',
      feedback: feedback?.trim() ? feedback : undefined,
      chatMessageId: feedbackState.chatMessageId!,
    })
    setFeedbackState({ state: 'closed' })
  }

  return (
    <motion.div className={cx('relative flex h-full bg-secondary-50 dark:bg-secondary-800 rounded-2xl overflow-hidden')}>
      <div
        className={cx(
          'absolute top-0 left-0 z-[1] w-full h-full transition-opacity opacity-0 bg-black pointer-events-none',
          feedbackState.state !== 'closed' && 'opacity-30'
        )}
      />
      <AnimatePresence>
        {activeSources && (
          <motion.div
            className={cx(
              'bg-white dark:bg-secondary-800 h-full p-4 overflow-y-auto flex flex-col gap-4 border-[1px] border-r-[0px] border-secondary-50 dark:border-secondary-800 max-h-[70vh]',
              'absolute top-0 left-0 z-10 w-full h-full rounded-2xl',
              'md:relative md:w-1/2 md:h-auto md:rounded-r-none'
            )}
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ ease: cubicBezier(0.25, 1, 0.5, 1) }}
          >
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => setActiveSources(null)}
                className="flex items-center text-neutral-600 hover:text-neutral-400 dark:text-secondary-200 gap-2"
              >
                <span className="text-base">close</span>
                <CloseIcon className="w-3 h-3" />
              </button>
            </div>
            {activeSources.documents.map((doc) => (
              <SourceLink key={doc.document_id} {...doc} />
            ))}
          </motion.div>
        )}
      </AnimatePresence>
      <div className={cx('flex flex-col gap-8 w-full h-full p-4 max-h-[70vh]')}>
        {conversation.length > 0 && (
          <div
            ref={scrollContainerRef}
            className="max-h-full overflow-y-auto scrollbar-transparent flex flex-col gap-6 py-8 px-4 flex-shrink"
          >
            {conversation.map((item, index) => {
              return (
                <div key={index} className={cx(item.from === 'user' && 'text-right')}>
                  {item.from === 'user' ? (
                    <ChatBubble {...item} />
                  ) : item.state === 'complete' ? (
                    <ChatBubble
                      {...item}
                      documents={item.response?.documents}
                      isSelected={
                        !!item.response?.finalMessage?.message_id &&
                        activeSources?.messageId === item.response?.finalMessage?.message_id
                      }
                      onViewSources={() => {
                        if (item.response?.finalMessage?.message_id) {
                          setActiveSources({
                            messageId: item.response?.finalMessage?.message_id,
                            documents: item.response?.documents ? dedupeDocuments(item.response?.documents) : [],
                          })
                        }
                      }}
                      onFeedback={(feedback) => {
                        setFeedbackState({ state: feedback, chatMessageId: item.response?.finalMessage?.message_id })
                      }}
                    />
                  ) : item.state === 'error' ? (
                    <ChatBubble {...item} />
                  ) : (
                    <StreamingMessage
                      message={item.message}
                      chatSessionId={sessionId.current!}
                      onComplete={handleComplete}
                      onError={handleError}
                      scrollToBottom={scrollToBottom}
                      parentMessageId={parentMessageId.current}
                    />
                  )}
                </div>
              )
            })}
          </div>
        )}
        <form className="w-full mx-auto" onSubmit={handleSubmit}>
          <div className={cx('relative', isLoading && 'opacity-80')}>
            <input
              ref={inputRef}
              name="search"
              type="text"
              placeholder="Ask a question"
              className={cx(
                'rounded-full w-full placeholder:text-xl text-xl pl-6 pr-32 py-2 h-16 border-2 ',
                'focus:outline-none focus:ring-0',
                !isLoading &&
                'bg-white dark:bg-secondary-800 dark:text-white placeholder:text-indigoBlue dark:placeholder:text-secondary-200 border-secondary-200 dark:border-secondary-700 focus:border-primaryBlue',
                isLoading && 'border-secondary-100 dark:border-secondary-700 dark:bg-secondary-800 dark:text-secondary-400 placeholder:text-secondary-400'
              )}
              autoComplete="off"
              disabled={isLoading}
              autoFocus
            />
            <button
              type="submit"
              className={cx(
                'absolute flex items-center gap-2 right-4 top-1/2 -translate-y-1/2 px-4 py-2 text-white rounded-full transition-colors',
                !isLoading && 'bg-primaryBlue hover:bg-indigoBlue',
                isLoading && 'bg-secondary-200 hover:bg-secondary-200 dark:bg-secondary-600 dark:hover:bg-secondary-600 cursor-not-allowed'
              )}
            >
              <span className="text-base">Send</span>
              <SendIcon className="w-4 h-4" />
            </button>
          </div>
        </form>
        {/* Feedback form */}
        <form
          className={cx(
            'absolute bottom-0 left-0 z-10 w-full transition-transform duration-300 max-h-full',
            feedbackState.state === 'closed' ? 'translate-y-full' : 'translate-y-0'
          )}
          onSubmit={handleSubmitFeedback}
        >
          <div
            className={cx(
              'relative flex flex-col gap-4 justify-center w-full rounded-t-2xl bg-gray-50 dark:bg-secondary-700 p-6 transition-opacity duration-300 shadow-sm',
              feedbackState.state === 'closed' ? 'opacity-0 pointer-events-none' : 'opacity-100'
            )}
          >
            <button
              type="button"
              onClick={() => setFeedbackState({ state: 'closed' })}
              className="absolute top-2 right-2 p-2"
            >
              <CloseIcon className="w-4 h-4" />
            </button>
            <h4 className="text-xl mb-0">
              {feedbackState.state === 'positive' ? 'Thank you for your feedback!' : 'We are sorry to hear that!'}
            </h4>
            <textarea
              name="feedback"
              className="w-full h-24 p-2 border-2 border-secondary-200 dark:border-secondary-600 rounded-md focus:outline-none focus:ring-0 max-h-full"
              placeholder={
                feedbackState.state === 'positive'
                  ? 'What did you like about the response? (Optional)'
                  : 'What did you find wrong with the reponse? (Optional)'
              }
            />
            <div className="flex items-center justify-center">
              <CtaLink asButton type="submit" style={buttonStyles.btnSolid} size="Base">
                Submit feedback
              </CtaLink>
            </div>
          </div>
        </form>
      </div>
    </motion.div>
  )
}
